import { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { ServiceStatisticsOccupancyDTO, ServiceStatisticsSummaryDTO } from '../../dto/ServiceStatisticsDTO';
import ChangeLine from '../../components/novarium/ChangeLine';
import InfoTip from '../../components/novarium/InfoTip';
import { modeMessageId, StatisticsMode } from './statisticsMode';
import {
    percentVariation, pointsVariation, previousPeriodLabel, Variation, Verdict, waitVerdict,
} from './statisticsChange';
import { compareOccupancy, formatInt, formatMinutes, formatPercent } from './statisticsUtils';

interface StatisticsKpisProps {
    summary: ServiceStatisticsSummaryDTO;
    /** Agendas de consultorios del filtro, para el KPI de ocupación. */
    agendas: ServiceStatisticsOccupancyDTO[];
    /** La ocupación no se pudo consultar; el resto de los indicadores sí. */
    occupancyFailed: boolean;
    /** En «Mi desempeño» los textos hablan de las atenciones y agendas del profesional. */
    mode?: StatisticsMode;
}

interface KpiCardProps {
    label: string;
    value: string;
    /** Renglones de detalle: la cantidad detrás del porcentaje, la variación contra el período anterior. */
    details?: ReactNode[];
    /** Frase corta que explica el indicador. */
    hint?: string;
}

function KpiCard({ label, value, details = [], hint }: KpiCardProps) {
    return (
        <div className="nv-card ss-kpi">
            <dt className="ss-kpi__label">
                {label}
                {hint && <InfoTip text={hint} />}
            </dt>
            <dd className="nv-kpi ss-kpi__value">{value}</dd>
            {details.length === 0 && <dd className="ss-kpi__detail">{' '}</dd>}
            {details.map((detail, index) => <dd key={index} className="ss-kpi__detail">{detail}</dd>)}
        </div>
    );
}

/**
 * Los cuatro indicadores del período: atenciones, espera media, cuántos esperaron más de una hora y ocupación. Cada uno
 * con su variación contra el período anterior de igual largo, escrita con las fechas de ese período. La duración media
 * está en la tabla de servicios. La espera media suma una línea con el percentil 90 («9 de cada 10 esperaron menos de…»).
 */
export default function StatisticsKpis({ summary, agendas, occupancyFailed, mode = 'management' }: StatisticsKpisProps) {
    const intl = useIntl();
    const { current, previous } = summary;
    const hasData = current.attentions > 0;
    const period = previousPeriodLabel(intl, summary, new Date());

    const line = (variation: Variation | null, verdict?: Verdict): ReactNode =>
        (variation ? <ChangeLine variation={variation} period={period} verdict={verdict} /> : undefined);

    const attentions = percentVariation(current.attentions, previous.attentions);
    const wait = percentVariation(current.avgWaitMinutes, previous.avgWaitMinutes);

    const over60 = hasData ? current.waitOver60 / current.attentions : null;
    const previousOver60 = previous.attentions > 0 ? previous.waitOver60 / previous.attentions : null;
    const over60Variation = pointsVariation(intl, over60, previousOver60);

    const { totals, comparableCurrent, previous: previousOccupancy } = compareOccupancy(agendas);
    const occupancyVariation = pointsVariation(intl, comparableCurrent.occupancy, previousOccupancy.occupancy);
    const occupancyValue = totals.occupancy === null || occupancyFailed ? '—' : formatPercent(totals.occupancy, 1);
    const occupancyNote: ReactNode = occupancyFailed
        ? intl.formatMessage({ id: 'ServicesStatisticsOccupancyFailedShort' })
        : totals.occupancy === null ? intl.formatMessage({ id: 'ServicesStatisticsKpiOccupancyOnlyRooms' })
            : line(occupancyVariation);

    return (
        <dl className="ss-kpis" aria-label={intl.formatMessage({ id: 'ServicesStatisticsKpisLabel' })}>
            <KpiCard
                label={intl.formatMessage({ id: modeMessageId(mode, 'KpiAttentions') })}
                value={hasData ? formatInt(current.attentions) : '—'}
                details={[line(attentions)]}
            />
            <KpiCard
                label={intl.formatMessage({ id: 'ServicesStatisticsKpiWait' })}
                hint={intl.formatMessage({ id: 'ServicesStatisticsKpiWaitHint' })}
                value={hasData ? formatMinutes(current.avgWaitMinutes) : '—'}
                details={[
                    ...(hasData && current.waitP90Minutes > 0
                        ? [intl.formatMessage({ id: 'ServicesStatisticsKpiWaitP90Line' }, { minutes: formatMinutes(current.waitP90Minutes) })]
                        : []),
                    line(wait, wait ? waitVerdict(wait.trend) : null),
                ]}
            />
            <KpiCard
                label={intl.formatMessage({ id: 'ServicesStatisticsKpiWait60' })}
                value={over60 === null ? '—' : formatPercent(over60)}
                details={[
                    hasData ? intl.formatMessage({ id: 'ServicesStatisticsKpiWait60Count' },
                        { count: current.waitOver60, countText: formatInt(current.waitOver60) }) : undefined,
                    line(over60Variation, over60Variation ? waitVerdict(over60Variation.trend) : null),
                ]}
            />
            <KpiCard
                label={intl.formatMessage({ id: 'ServicesStatisticsKpiOccupancy' })}
                hint={intl.formatMessage({ id: modeMessageId(mode, 'KpiOccupancyHint') })}
                value={occupancyValue}
                details={[occupancyNote]}
            />
        </dl>
    );
}
